import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { LESSONS, lessonByKey } from "./lessons";

/**
 * Страница урока образовательного курса.
 *
 * Формат: вступление, блоки с содержанием, разворот из печатного гайда как
 * иллюстрация, вывод и кнопка «Урок пройден».
 *
 * Отметка о прохождении пока хранится в браузере: ручки, которая записала бы
 * её на пользователя, нет. Это честнее, чем не отмечать вовсе — человек хотя
 * бы видит, где остановился, — но при смене устройства прогресс потеряется.
 * Когда появится ручка, меняется одна функция.
 */

const PUBLIC = process.env.PUBLIC_URL || "";
const DONE_KEY = "cpai_lessons_done";

const readDone = () => {
  try {
    const raw = localStorage.getItem(DONE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
};

const LessonPage = () => {
  const { key } = useParams();
  const lesson = lessonByKey(key);
  const [done, setDone] = useState(() => readDone().includes(key));

  const markDone = () => {
    const list = readDone();
    if (!list.includes(key)) {
      list.push(key);
      try {
        localStorage.setItem(DONE_KEY, JSON.stringify(list));
      } catch (e) {
        /* приватный режим — отметка не сохранится, но урок засчитаем на экране */
      }
    }
    setDone(true);
  };

  if (!lesson) {
    return (
      <div className="fw-page">
        <Link className="fw-back" to={`${PUBLIC}/learning`}>
          ← К курсу
        </Link>
        <h1 className="fw-title">Урок не найден</h1>
        <p className="fw-sub">Возможно, ссылка устарела. Откройте список уроков в курсе.</p>
      </div>
    );
  }

  const next = LESSONS[LESSONS.findIndex((l) => l.key === key) + 1] || null;

  return (
    <div className="fw-page fw-lesson">
      <Link className="fw-back" to={`${PUBLIC}/learning`}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 18l-6-6 6-6" />
        </svg>
        К курсу
      </Link>

      <div className="fw-lesson-head">
        <span className="fw-chip">
          Урок {lesson.n} · {lesson.minutes} мин
        </span>
        <h1 className="fw-title">{lesson.title}</h1>
        <p className="fw-sub mb-0">{lesson.lead}</p>
      </div>

      {lesson.image && (
        <figure className="fw-lesson-figure">
          <img src={lesson.image} alt={lesson.imageAlt || ""} />
          <figcaption>Тот же разворот в печатном гайде — можно открыть крупнее.</figcaption>
        </figure>
      )}

      <div className="fw-lesson-blocks">
        {lesson.blocks.map((b, i) => (
          <section className="fw-card fw-lesson-block" key={b.title}>
            <span className="n">{i + 1}</span>
            <div>
              <b>{b.title}</b>
              <p>{b.text}</p>
            </div>
          </section>
        ))}
      </div>

      <section className="fw-card fw-lesson-outcome">
        <b>Что дальше</b>
        <p>{lesson.outcome}</p>

        <div className="fw-lesson-acts">
          {done ? (
            <span className="fw-tag ok">Урок пройден</span>
          ) : (
            <button type="button" className="fw-btn" onClick={markDone}>
              Урок пройден
            </button>
          )}

          {next ? (
            <Link className="fw-btn ghost" to={`${PUBLIC}/learning/${next.key}`}>
              Следующий урок →
            </Link>
          ) : (
            <Link className="fw-btn ghost" to={`${PUBLIC}/learning`}>
              Ко всем урокам
            </Link>
          )}
        </div>
      </section>
    </div>
  );
};

export default LessonPage;
